//------------------------------------------------------
// Image Quality
//
// One pass over a frame answering three separate questions:
//
//   how bright is it   -> drives the torch / exposure (whole-frame mean luma)
//   is it in focus     -> gates the upload (Laplacian + Tenengrad + glare)
//   is the LENS dirty  -> the 4x4 grid below
//
// The focus verdict is taken on a 4x4 grid rather than on the car box as a
// whole. A single number over the whole box is dominated by whatever is sharp
// in it: a clean windscreen and a smeared door average out to "fine", and the
// smear is never reported. Scoring sixteen cells independently and counting how
// many fail keeps a local problem local, which is the shape of every real cause
// -- a fingerprint, a water drop, a scratch.
//
// Glare is measured the same way and for the same reason. A bright patch
// destroys the gradients focus is measured from, so a glared cell reads as
// blurry whether or not it is; the binary-variance score below is what lets the
// two be told apart, by relaxing the Laplacian bar on cells that are merely
// washed out. Headlights and taillights are excluded from it -- they are
// supposed to be bright, and counting them would call every night-time front
// shot glared.
//------------------------------------------------------

// Focus / glare cuts. Shared by the whole-box pass and by every grid cell, so a
// cell is judged on exactly the terms the box is.
//
//   LAPLACE_1   below this is blurry outright
//   LAPLACE_2   below this is blurry ONLY when the region is also glared: the
//               gradients are there but the highlight is eating them
//   TENENGRAD   edge energy; low means nothing in the region has a hard edge
//   GLARE       binary variance of the bright-pixel ratio, above which the
//               region counts as glared
export const LAPLACE_THRESHOLD_1 = 630;
export const LAPLACE_THRESHOLD_2 = 900;
export const TENENGRAD_THRESHOLD = 12500;
export const GLARE_THRESHOLD = 2000;

// Pixels above this count as "bright" for the glare score.
const GLARE_BRIGHT_LEVEL = 200;

// The grid. 4x4 = 16 cells, and two bars on how many of them fail:
//
//   GRID_BLURRY_CELLS        14 -- the frame is blurred and is NOT uploaded.
//                            Strict on purpose: a handful of bad cells is
//                            ordinary -- a dark wheel arch, a flat panel with no
//                            detail to resolve, a glared panel -- and gating on
//                            that would block uploads on frames that are usable.
//   GRID_POPUP_BLURRY_CELLS  10 -- the frame counts as blurry for the 10 second
//                            dirty-lens window. Looser, so a smeared lens is
//                            caught before it gets bad enough to drop frames.
export const GRID_ROWS = 4;
export const GRID_COLS = 4;
export const GRID_CELLS = GRID_ROWS * GRID_COLS;
export const GRID_BLURRY_CELLS = 14;
export const GRID_POPUP_BLURRY_CELLS = 10;

/** Clip a bbox to the image, returning a NEW object (never mutates the input). */
const clipBBox = (bbox, imgWidth, imgHeight) => {
  const x = Math.max(0, Math.round(bbox.x));
  const y = Math.max(0, Math.round(bbox.y));
  return {
    x,
    y,
    width: Math.max(1, Math.min(Math.round(bbox.width), imgWidth - x)),
    height: Math.max(1, Math.min(Math.round(bbox.height), imgHeight - y)),
  };
};

/**
 * Shrink the crop to cropWidth x cropHeight and return it as grayscale, using
 * our own averaging rather than the browser's.
 *
 * This replaced a ctx.drawImage onto a smaller canvas. That hands the resample
 * to the engine, and every engine does it differently: on the same 48 test
 * frames Firefox called 39 blurry where Chrome and Edge called 47, and Safari
 * -- which is every browser on iPhone -- is a third implementation again. Focus
 * is measured from the gradients between neighbouring pixels, so a different
 * filter is a different score, and the verdict became a property of the user's
 * browser instead of a property of the photo.
 *
 * Each destination pixel is the plain mean of the source pixels it covers. The
 * boxes tile the crop exactly -- every source pixel is counted once, none is
 * skipped -- and the arithmetic is ordinary float maths, so every engine
 * produces the same number.
 *
 * Luma is taken per source pixel and then averaged, which is the same value as
 * averaging R, G and B and taking the luma of that: luma is linear in all
 * three. One accumulator instead of three.
 *
 * Reads the full-resolution frame the brightness pass already holds, so the
 * second canvas, drawImage and getImageData this used to need are gone.
 * brightnessSum and darkPixels come back with it because the crop is already
 * being walked here; counting them in a second pass would read every pixel
 * twice for nothing.
 */
const downscaleCropToGray = (
    fullData,
    imgWidth,
    clipped,
    cropWidth,
    cropHeight,
    darkThreshold
) => {

    const gray = new Uint8Array(cropWidth * cropHeight);

    let brightnessSum = 0;
    let darkPixels = 0;

    // Source edges per destination column and row, precomputed so the inner
    // loops are lookups rather than a divide per pixel.
    const xEdges = new Int32Array(cropWidth + 1);
    for (let dx = 0; dx <= cropWidth; dx++)
        xEdges[dx] = Math.floor((dx * clipped.width) / cropWidth);

    const yEdges = new Int32Array(cropHeight + 1);
    for (let dy = 0; dy <= cropHeight; dy++)
        yEdges[dy] = Math.floor((dy * clipped.height) / cropHeight);

    for (let dy = 0; dy < cropHeight; dy++) {

        const sy0 = yEdges[dy];
        // A box is never empty. The crop is only ever shrunk, so the edges are
        // already at least a pixel apart and this cannot fire -- it is here so
        // an empty box could never divide by zero if that ever changed.
        const sy1 = Math.min(
            clipped.height,
            Math.max(sy0 + 1, yEdges[dy + 1])
        );

        for (let dx = 0; dx < cropWidth; dx++) {

            const sx0 = xEdges[dx];
            const sx1 = Math.min(
                clipped.width,
                Math.max(sx0 + 1, xEdges[dx + 1])
            );

            let sum = 0;
            let n = 0;

            for (let sy = sy0; sy < sy1; sy++) {

                // Start of this box's row in the FULL frame: the crop is a
                // window into it, never a copy of it.
                let i =
                    (((clipped.y + sy) * imgWidth) +
                        clipped.x +
                        sx0) * 4;

                for (let sx = sx0; sx < sx1; sx++, i += 4) {
                    sum +=
                        0.299 * fullData[i] +
                        0.587 * fullData[i + 1] +
                        0.114 * fullData[i + 2];
                    n++;
                }
            }

            const g = sum / n;

            gray[dy * cropWidth + dx] = g;
            brightnessSum += g;

            if (g < darkThreshold) darkPixels++;
        }
    }

    return { gray, brightnessSum, darkPixels };
};

/**
 * Turn a frame's detections into the boxes this module wants.
 *
 * `car` is the union of every detected part, in pixels, which is the region
 * worth judging; with nothing detected it is the whole frame, so the checks
 * keep running rather than going blind between detections. van / non_van are
 * whole-vehicle classifications rather than parts and are skipped -- they would
 * stretch the union over the entire frame and undo the crop.
 *
 * Headlights and taillights come back separately so the glare score can ignore
 * them.
 */
export const boxesFromDetections = (detections, videoW, videoH) => {
  const full = { x: 0, y: 0, width: videoW, height: videoH };
  const headlightBoxes = [];
  const taillightBoxes = [];

  if (!detections || !detections.length) {
    return { car: full, headlightBoxes, taillightBoxes };
  }

  let x1 = 1;
  let y1 = 1;
  let x2 = 0;
  let y2 = 0;
  let seen = 0;

  detections.forEach((d) => {
    if (!d || !d.bbox) return;
    if (d.label === "van" || d.label === "non_van") return;
    const [x, y, w, h] = d.bbox;
    if ([x, y, w, h].some((v) => typeof v !== "number" || !isFinite(v))) return;
    seen += 1;
    x1 = Math.min(x1, x);
    y1 = Math.min(y1, y);
    x2 = Math.max(x2, x + w);
    y2 = Math.max(y2, y + h);

    if (d.label === "headlight" || d.label === "taillight") {
      const px = {
        x: x * videoW,
        y: y * videoH,
        width: w * videoW,
        height: h * videoH,
      };
      if (d.label === "headlight") headlightBoxes.push(px);
      else taillightBoxes.push(px);
    }
  });

  if (!seen || x2 <= x1 || y2 <= y1) {
    return { car: full, headlightBoxes, taillightBoxes };
  }

  return {
    car: {
      x: x1 * videoW,
      y: y1 * videoH,
      width: (x2 - x1) * videoW,
      height: (y2 - y1) * videoH,
    },
    headlightBoxes,
    taillightBoxes,
  };
};

/*
 * Both controllers analyse the same frame on the same tick -- one for
 * brightness, one for focus -- and the analysis is the most expensive thing in
 * the detect loop. Rather than have them coordinate, the last result is kept
 * and handed to the second caller.
 *
 * Keyed on the frame data itself, so a hit is the same frame by definition, not
 * a guess. Exactly one entry: the point is to serve the second read of the
 * current frame, and the next frame's key will not match anyway.
 */
let memoKey = "";
let memoValue = null;

const cacheKey = (src, bbox, opts) =>
  `${src.length}|${src.slice(-64)}|${bbox.x},${bbox.y},${bbox.width},${bbox.height}|` +
  `${opts.maxSize},${opts.darkThreshold},${opts.headlightBoxes.length},${opts.taillightBoxes.length}`;

export async function analyzeImageQuality(
    base64,
    bbox,
    {
        maxSize = 200,
        darkThreshold = 50,
        headlightBoxes = [],
        taillightBoxes = [],
        useCache = true
    } = {}
) {

    const src = base64.startsWith("data:")
        ? base64
        : `data:image/jpeg;base64,${base64}`;

    const key = useCache
        ? cacheKey(src, bbox, { maxSize, darkThreshold, headlightBoxes, taillightBoxes })
        : "";

    if (key && key === memoKey && memoValue) return memoValue;

    const run = new Promise((resolve, reject) => {

        const img = new Image();

        img.onload = () => {

            try {

                //----------------------------------
                // Overall Image Light
                //
                // Whole frame, full resolution. This is the number the torch and
                // the exposure loop act on: they change the light in the room,
                // not the light on one panel, so a crop would have them chasing
                // whatever happens to be in frame.
                //----------------------------------

                const fullCanvas = document.createElement("canvas");

                fullCanvas.width = img.width;
                fullCanvas.height = img.height;

                const fullCtx = fullCanvas.getContext(
                    "2d",
                    { willReadFrequently: true }
                );

                fullCtx.drawImage(img, 0, 0);

                const fullData = fullCtx.getImageData(
                    0,
                    0,
                    img.width,
                    img.height
                ).data;

                let overallBrightnessSum = 0;
                let overallDarkPixels = 0;

                const overallTotalPixels = fullData.length / 4;

                for (let i = 0; i < fullData.length; i += 4) {

                    const r = fullData[i];
                    const g = fullData[i + 1];
                    const b = fullData[i + 2];

                    const brightness =
                        0.299 * r +
                        0.587 * g +
                        0.114 * b;

                    overallBrightnessSum += brightness;

                    if (brightness < darkThreshold)
                        overallDarkPixels++;
                }

                const overallBrightness =
                    overallBrightnessSum / overallTotalPixels;

                const overallDarkPercent =
                    (overallDarkPixels / overallTotalPixels) * 100;

                const overallLowLight =
                    overallBrightness < 80 ||
                    overallDarkPercent > 50;

                //--------------------------------------------------
                // Clip Bounding Box
                //
                // Into a new object. The caller's bbox is left alone -- it is
                // reused across checks, and quietly rewriting it here made the
                // second reader see a box the first had already clipped.
                //--------------------------------------------------

                const clipped = clipBBox(bbox, img.width, img.height);

                //----------------------------------
                // Ambient (surrounding) light
                //----------------------------------

                const padding = 0.25;

                const outer = {
                    x: Math.max(0, Math.round(clipped.x - clipped.width * padding)),
                    y: Math.max(0, Math.round(clipped.y - clipped.height * padding))
                };

                outer.width = Math.min(
                    img.width - outer.x,
                    Math.round(clipped.width * (1 + 2 * padding))
                );


                outer.height = Math.min(
                    img.height - outer.y,
                    Math.round(clipped.height * (1 + 2 * padding))
                );

                const ambientCanvas = document.createElement("canvas");

                ambientCanvas.width = outer.width;
                ambientCanvas.height = outer.height;

                const ambientCtx = ambientCanvas.getContext(
                    "2d",
                    { willReadFrequently: true }
                );

                ambientCtx.drawImage(
                    img,
                    outer.x,
                    outer.y,
                    outer.width,
                    outer.height,
                    0,
                    0,
                    outer.width,
                    outer.height
                );

                const ambientData =
                    ambientCtx.getImageData(
                        0,
                        0,
                        outer.width,
                        outer.height
                    ).data;
                const innerX = clipped.x - outer.x;
                const innerY = clipped.y - outer.y;

                let ambientSum = 0;
                let ambientDark = 0;
                let ambientCount = 0;

                for (let y = 0; y < outer.height; y++) {

                    for (let x = 0; x < outer.width; x++) {

                        // Skip car pixels
                        if (
                            x >= innerX &&
                            x < innerX + clipped.width &&
                            y >= innerY &&
                            y < innerY + clipped.height
                        ) {
                            continue;
                        }

                        const idx = (y * outer.width + x) * 4;

                        const r = ambientData[idx];
                        const g = ambientData[idx + 1];
                        const b = ambientData[idx + 2];

                        const brightness =
                            0.299 * r +
                            0.587 * g +
                            0.114 * b;

                        ambientSum += brightness;

                        if (brightness < darkThreshold)
                            ambientDark++;

                        ambientCount++;
                    }
                }

                // With no detections the caller passes a full-frame bbox, so
                // every pixel is skipped as "car" and there is no ring left to
                // measure. Report null rather than 0/0 = NaN.
                const hasAmbient = ambientCount > 0;

                const ambientBrightness = hasAmbient
                    ? ambientSum / ambientCount
                    : null;

                const ambientDarkPercent = hasAmbient
                    ? (ambientDark / ambientCount) * 100
                    : null;

                const ambientLowLight = hasAmbient
                    ? ambientBrightness < 90 ||
                      ambientDarkPercent > 50
                    : null;

                //--------------------------------------------------
                // Resize crop
                //
                // Down to maxSize on the long edge, by our own box filter --
                // see downscaleCropToGray. Handing the resample to the browser,
                // which is what a scaled ctx.drawImage does, made the verdict a
                // property of the browser rather than of the photo.
                //--------------------------------------------------

                const scale = Math.min(
                    1,
                    maxSize /
                    Math.max(
                        clipped.width,
                        clipped.height
                    )
                );

                const cropWidth =
                    Math.max(
                        1,
                        Math.round(
                            clipped.width * scale
                        )
                    );

                const cropHeight =
                    Math.max(
                        1,
                        Math.round(
                            clipped.height * scale
                        )
                    );

                //--------------------------------------------------
                // Prepare grayscale
                //
                // Built once and shared by every measurement below, box and
                // cell alike. Cropping each cell separately would cost sixteen
                // resizes and, worse, would give each cell its own edges: a
                // pixel on an internal cell boundary would lose the neighbours
                // it actually has. Slicing one array keeps that context.
                //--------------------------------------------------

                const {
                    gray,
                    brightnessSum,
                    darkPixels
                } = downscaleCropToGray(
                    fullData,
                    img.width,
                    clipped,
                    cropWidth,
                    cropHeight,
                    darkThreshold
                );

                //--------------------------------------------------
                // Which cell each pixel belongs to
                //--------------------------------------------------

                const tileW = cropWidth / GRID_COLS;
                const tileH = cropHeight / GRID_ROWS;

                // Cell edges, so a pixel's cell is a lookup rather than a
                // divide, and so the cell a pixel is counted in is exactly the
                // cell whose bounds are reported.
                const colEdges = [];
                for (let c = 0; c <= GRID_COLS; c++)
                    colEdges.push(Math.round(c * tileW));
                const rowEdges = [];
                for (let r = 0; r <= GRID_ROWS; r++)
                    rowEdges.push(Math.round(r * tileH));

                const colOf = new Int8Array(cropWidth);
                for (let c = 0; c < GRID_COLS; c++)
                    for (let x = colEdges[c]; x < colEdges[c + 1]; x++)
                        colOf[x] = c;
                const rowOf = new Int8Array(cropHeight);
                for (let r = 0; r < GRID_ROWS; r++)
                    for (let y = rowEdges[r]; y < rowEdges[r + 1]; y++)
                        rowOf[y] = r;

                //--------------------------------------------------
                // Focus: Laplacian variance + Tenengrad
                //
                // One pass feeding both the whole-box totals and the per-cell
                // totals. The cells tile the box exactly, so the box figures
                // are the sums of the cell figures rather than a second,
                // separately-computed answer that could disagree with them.
                //--------------------------------------------------

                const lapSum = new Float64Array(GRID_CELLS);
                const lapSumSq = new Float64Array(GRID_CELLS);
                const lapCount = new Float64Array(GRID_CELLS);
                const tenSum = new Float64Array(GRID_CELLS);

                let sum = 0;
                let sumSq = 0;
                let count = 0;
                let tenengradScore = 0;

                for (let y = 1; y < cropHeight - 1; y++) {

                    const rowBase = rowOf[y] * GRID_COLS;

                    for (let x = 1; x < cropWidth - 1; x++) {

                        const c = y * cropWidth + x;

                        // 4-neighbour Laplacian
                        const lap =
                            gray[c - cropWidth] +
                            gray[c - 1] +
                            gray[c + 1] +
                            gray[c + cropWidth] -
                            4 * gray[c];

                        // Sobel X
                        const gx =
                            -gray[c - cropWidth - 1] +
                            gray[c - cropWidth + 1] +
                            -2 * gray[c - 1] +
                            2 * gray[c + 1] +
                            -gray[c + cropWidth - 1] +
                            gray[c + cropWidth + 1];

                        // Sobel Y
                        const gy =
                            -gray[c - cropWidth - 1]
                            - 2 * gray[c - cropWidth]
                            - gray[c - cropWidth + 1]
                            + gray[c + cropWidth - 1]
                            + 2 * gray[c + cropWidth]
                            + gray[c + cropWidth + 1];

                        // Gradient magnitude squared
                        const ten = gx * gx + gy * gy;

                        sum += lap;
                        sumSq += lap * lap;
                        count++;
                        tenengradScore += ten;

                        const cell = rowBase + colOf[x];
                        lapSum[cell] += lap;
                        lapSumSq[cell] += lap * lap;
                        lapCount[cell]++;
                        tenSum[cell] += ten;

                    }

                }

                // A crop under 3px in either direction leaves the loop above
                // with nothing to measure. Report zeros rather than NaN, and
                // let the "measured" flags below say the frame could not be
                // judged.
                const mean = count ? sum / count : 0;

                const blurScore = count
                    ? (sumSq / count) - (mean * mean)
                    : 0;

                tenengradScore = count ? tenengradScore / count : 0;

                //--------------------------------------------------
                // Glare
                //
                // Share of bright pixels, as a binary variance so a region that
                // is uniformly bright (a white car in daylight) scores low and
                // only a region that is PART blown out scores high -- which is
                // what actually destroys the gradients focus is measured from.
                //
                // Headlights and taillights are excluded: they are supposed to
                // be bright, and counting them would flag every night-time
                // front or rear shot.
                //--------------------------------------------------

                const excludedBoxes = [...headlightBoxes, ...taillightBoxes]
                    .filter((b) => b && isFinite(b.x) && isFinite(b.y))
                    .map((box) => ({
                        x: (box.x - clipped.x) * scale,
                        y: (box.y - clipped.y) * scale,
                        width: box.width * scale,
                        height: box.height * scale
                    }));

                const brightCount = new Float64Array(GRID_CELLS);
                const validCount = new Float64Array(GRID_CELLS);

                let brightPixels = 0;
                let validPixels = 0;

                for (let y = 0; y < cropHeight; y++) {

                    const rowBase = rowOf[y] * GRID_COLS;

                    for (let x = 0; x < cropWidth; x++) {

                        let excluded = false;
                        for (let k = 0; k < excludedBoxes.length; k++) {
                            const box = excludedBoxes[k];
                            if (
                                x >= box.x &&
                                x <= box.x + box.width &&
                                y >= box.y &&
                                y <= box.y + box.height
                            ) {
                                excluded = true;
                                break;
                            }
                        }
                        if (excluded) continue;

                        const cell = rowBase + colOf[x];
                        const bright = gray[y * cropWidth + x] > GLARE_BRIGHT_LEVEL;

                        validPixels++;
                        validCount[cell]++;
                        if (bright) {
                            brightPixels++;
                            brightCount[cell]++;
                        }

                    }

                }

                const varianceOf = (bright, valid) => {
                    const p = valid > 0 ? bright / valid : 0;
                    return Number((p * (1 - p) * 255 * 255).toFixed(2));
                };

                const binaryVariance = varianceOf(brightPixels, validPixels);
                const glare = binaryVariance > GLARE_THRESHOLD;

                //--------------------------------------------------
                // Per-cell verdicts
                //
                // Same rule as the box, applied to each cell on its own
                // unrounded score. A cell with nothing to measure -- too small,
                // or wholly inside an excluded headlight -- is reported
                // unmeasured and never counted as blurry: "cannot tell" is not
                // evidence of a dirty lens.
                //--------------------------------------------------

                const grid = [];
                let blurryCells = 0;
                let glaredCells = 0;
                let measuredCells = 0;

                for (let r = 0; r < GRID_ROWS; r++) {
                    for (let cIdx = 0; cIdx < GRID_COLS; cIdx++) {

                        const i = r * GRID_COLS + cIdx;
                        const n = lapCount[i];
                        const measured = n > 0;

                        const cellMean = measured ? lapSum[i] / n : 0;
                        const cellLapRaw = measured
                            ? (lapSumSq[i] / n) - (cellMean * cellMean)
                            : 0;
                        const cellTen = measured ? tenSum[i] / n : 0;
                        const cellVar = varianceOf(brightCount[i], validCount[i]);
                        const cellGlare = cellVar > GLARE_THRESHOLD;

                        const cellBlurry =
                            measured &&
                            (cellLapRaw < LAPLACE_THRESHOLD_1 ||
                                cellTen < TENENGRAD_THRESHOLD ||
                                (cellLapRaw < LAPLACE_THRESHOLD_2 && cellGlare));

                        if (measured) measuredCells++;
                        if (cellBlurry) blurryCells++;
                        if (cellGlare) glaredCells++;

                        grid.push({
                            row: r,
                            col: cIdx,
                            // In ORIGINAL image coordinates, so a caller can
                            // draw the cell over the live frame without having
                            // to know how the crop was scaled.
                            bbox: {
                                x: Math.round(clipped.x + colEdges[cIdx] / scale),
                                y: Math.round(clipped.y + rowEdges[r] / scale),
                                width: Math.round(
                                    (colEdges[cIdx + 1] - colEdges[cIdx]) / scale
                                ),
                                height: Math.round(
                                    (rowEdges[r + 1] - rowEdges[r]) / scale
                                ),
                            },
                            measured,
                            blurScore: Math.round(cellLapRaw),
                            tenengradScore: Number(cellTen.toFixed(2)),
                            binaryVariance: cellVar,
                            glare: cellGlare,
                            blurry: cellBlurry,
                        });

                    }
                }

                //--------------------------------------------------
                // Lighting
                //--------------------------------------------------

                const totalPixels =
                    cropWidth * cropHeight;

                const avgBrightness =
                    brightnessSum / totalPixels;

                const darkPixelPercent =
                    (darkPixels / totalPixels) * 100;

                //--------------------------------------------------
                // Thresholds
                //--------------------------------------------------

                // Whole-box verdict, kept as a diagnostic: it is what the old
                // single-number check would have said, so a sheet row shows
                // where the grid and the box disagree.
                const boxBlurry =
                    count > 0 &&
                    (blurScore < LAPLACE_THRESHOLD_1 ||
                        tenengradScore < TENENGRAD_THRESHOLD ||
                        (blurScore < LAPLACE_THRESHOLD_2 && glare));

                // The verdict the upload gate and the dial act on: 14 of the 16
                // grid cells or more came back blurry.
                const blurry = blurryCells >= GRID_BLURRY_CELLS;
                // The verdict the dirty-lens popup's window counts: 10 or more.
                const popupBlurry = blurryCells >= GRID_POPUP_BLURRY_CELLS;

                const lowLight =
                    avgBrightness < 70 ||
                    darkPixelPercent > 50;

                //--------------------------------------------------
                // Return
                //--------------------------------------------------

                resolve({

                    tenengradScore: Number(tenengradScore.toFixed(2)),

                    blurScore:
                        Math.round(blurScore),

                    binaryVariance,
                    glare,

                    blurry,
                    popupBlurry,
                    boxBlurry,

                    grid,
                    gridRows: GRID_ROWS,
                    gridCols: GRID_COLS,
                    gridCells: GRID_CELLS,
                    blurryCells,
                    glaredCells,
                    measuredCells,
                    blurryCellsNeeded: GRID_BLURRY_CELLS,
                    popupBlurryCellsNeeded: GRID_POPUP_BLURRY_CELLS,

                    avgBrightness:
                        Number(
                            avgBrightness.toFixed(1)
                        ),

                    darkPixelPercent:
                        Number(
                            darkPixelPercent.toFixed(1)
                        ),

                    lowLight,
                    ambientBrightness: hasAmbient
                        ? Number(ambientBrightness.toFixed(1))
                        : null,
                    ambientDarkPercent: hasAmbient
                        ? Number(ambientDarkPercent.toFixed(1))
                        : null,
                    ambientLowLight,

                    overallBrightness: Number(overallBrightness.toFixed(1)),
                    overallDarkPercent: Number(overallDarkPercent.toFixed(1)),
                    overallLowLight


                });

            }

            catch (err) {

                reject(err);

            }

        };

        img.onerror = () =>
            reject(
                new Error(
                    "Could not load image."
                )
            );

        img.src = src;

    });

    if (key) {
        memoKey = key;
        // Cache the promise, not the result: the second caller usually arrives
        // while the first is still decoding, and caching only on resolve would
        // miss exactly that case.
        memoValue = run;
        // A rejected analysis must not be served to the next caller.
        run.catch(() => {
            if (memoValue === run) {
                memoKey = "";
                memoValue = null;
            }
        });
    }

    return run;

}

window.analyzeImageQuality =
    analyzeImageQuality;
